import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { AndroidSettings, IOSSettings, NativeSettings } from "capacitor-native-settings";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";

import { isNativeApp } from "@/utilities/capacitorLogic";
import { isNotificationsDisallowedError } from "@/utilities/pushNotificationErrors";

const STORAGE_KEY = "notification_permission_modal_last_shown_at";
const HIDE_DAYS = 7;

const getNowIsoString = () => new Date().toISOString();

const openNotificationSettings = async () => {
  const platform = Capacitor.getPlatform();

  if (platform === "ios") {
    await NativeSettings.openIOS({ option: IOSSettings.App });
    return;
  }

  if (platform === "android") {
    await NativeSettings.openAndroid({ option: AndroidSettings.AppNotification });
    return;
  }
};

const shouldSkipFromCooldown = () => {
  if (typeof window === "undefined") return true;
  const lastShownAt = window.localStorage.getItem(STORAGE_KEY);
  if (!lastShownAt) return false;
  const diffDays = dayjs().diff(dayjs(lastShownAt), "day");
  return diffDays < HIDE_DAYS;
};

const markShownNow = () => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, getNowIsoString());
};

const getPermissionState = async (): Promise<string | null> => {
  try {
    const status = await PushNotifications.checkPermissions();
    // Capacitor v5 returns { receive: PermissionState, display?: PermissionState }
    return status.receive ?? null;
  } catch (error) {
    console.error("push permission check failed", error);
    return null;
  }
};

export const useNotificationPermissionModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [checking, setChecking] = useState(true);
  const [requesting, setRequesting] = useState(false);

  const registerPushNotifications = useCallback(async () => {
    if (!isNativeApp) return;
    try {
      await PushNotifications.register();
    } catch (registerError) {
      // MEMO
      // register() は iOS / Android とも内部で即 resolve するため、この catch には登録失敗が届かない
      // ネットワーク起因も権限未許可も含め、APNs / FCM の登録失敗はすべて
      // registrationError リスナーを持つ capacitorLogic.ts の initializePushNotification 側で扱っている
      // （＝下の権限未許可の分岐も現状は到達しない。ここに来るのは Capacitor ブリッジ側の失敗のみ）
      // ※ @capacitor/push-notifications 8.1.1 時点の実装に基づく。更新時は register() が reject し得ないか再確認すること
      if (isNotificationsDisallowedError(registerError)) {
        console.info("push register skipped because notifications are disallowed");
        return;
      }
      console.error("push register failed", registerError);
    }
  }, []);

  const refreshPermissionState = useCallback(async () => {
    if (!isNativeApp) return null;

    const state = await getPermissionState();

    if (state === "granted") {
      await registerPushNotifications();
      setIsOpen(false);
      return state;
    }

    if (shouldSkipFromCooldown()) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }

    return state;
  }, [registerPushNotifications]);

  const ensureShownCooldown = useCallback(() => {
    markShownNow();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    ensureShownCooldown();
  }, [isOpen, ensureShownCooldown]);

  useEffect(() => {
    if (!isNativeApp) {
      setChecking(false);
      return;
    }

    let mounted = true;

    const prepare = async () => {
      try {
        await refreshPermissionState();
      } finally {
        if (mounted) {
          setChecking(false);
        }
      }
    };

    prepare();

    return () => {
      mounted = false;
    };
  }, [refreshPermissionState]);

  useEffect(() => {
    if (!isNativeApp) return;

    let cancelled = false;

    const subscription = App.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) return;
      refreshPermissionState();
    });

    return () => {
      cancelled = true;
      subscription
        .then((handle) => {
          if (!cancelled) return;
          handle.remove();
        })
        .catch((error) => {
          console.error("app state listener cleanup failed", error);
        });
    };
  }, [refreshPermissionState]);

  const requestPermission = useCallback(async () => {
    if (!isNativeApp) {
      setIsOpen(false);
      return;
    }

    setRequesting(true);
    try {
      const state = await refreshPermissionState();
      if (state === "granted") {
        ensureShownCooldown();
        return;
      }

      await openNotificationSettings();
      ensureShownCooldown();
      setIsOpen(false);
    } catch (error) {
      console.error("push permission request failed", error);
      ensureShownCooldown();
      setIsOpen(false);
    } finally {
      setRequesting(false);
    }
  }, [ensureShownCooldown, refreshPermissionState]);

  const handleClose = useCallback(() => {
    ensureShownCooldown();
    setIsOpen(false);
  }, [ensureShownCooldown]);

  return useMemo(
    () => ({
      checking,
      open: !checking && isOpen,
      requesting,
      requestPermission,
      handleClose,
    }),
    [checking, isOpen, requesting, requestPermission, handleClose]
  );
};

export default useNotificationPermissionModal;
